/**
 * Validation functions for BGP configuration fields
 */

import { formatCiscoUptime } from './formattersShapedData';
import { isClusterMemberUp, type ClusterMemberCpr } from '../components/BGP/helpers/bgpVisibilityApi';
import type { BgpNeighborRoster, CloudResourceTag, TagAttribution } from '../components/BGP/BgpVisibilityData';

/**
 * Cloud type for validation
 */
export type Cloud = "aws" | "oci" | "azure" | "gcp" | "unknown";

/**
 * Validation function for routing table IDs
 * Validates AWS, OCI, Azure, and GCP routing table formats
 * @param input - The routing table ID to validate
 * @returns Object with valid boolean and cloud type
 */
export function validateRtb(input: string): { valid: boolean; cloud: Cloud } {
  const v = input.trim();

  if (!v) return { valid: false, cloud: "unknown" };

  // AWS: rtb-xxxxxxxx o rtb-xxxxxxxxxxxxxxxxx
  if (/^rtb-(?:[0-9a-f]{8}|[0-9a-f]{17})$/i.test(v))
    return { valid: true, cloud: "aws" };

  // OCI: ocid1.routetable....
  if (/^ocid1\.routetable\./i.test(v))
    return { valid: true, cloud: "oci" };

  // Azure: ARM ID route table
  if (/^\/subscriptions\/.+\/providers\/Microsoft\.Network\/routeTables\/.+/i.test(v))
    return { valid: true, cloud: "azure" };

  // GCP: selfLink o nombre válido
  if (
    /^https:\/\/www\.googleapis\.com\/compute\/v1\/projects\/.+\/global\/routes\/.+/i.test(v) ||
    /^[a-z]([-a-z0-9]{0,61}[a-z0-9])?$/i.test(v)
  )
    return { valid: true, cloud: "gcp" };

  return { valid: false, cloud: "unknown" };
}

/**
 * Generic validation function for number-only fields
 * @param value - The value to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateNumberField = (value: string): string => {
  if (!value) return ""; // Empty is valid (will be validated by required check)
  const validPattern = /^[0-9]*$/;
  if (!validPattern.test(value)) {
    return "Invalid format";
  }
  return "";
};

/**
 * Validation function for hold-time: min 3, max 65535
 * @param value - The hold time value to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateHoldTime = (value: string): string => {
  if (!value) return ""; // Empty is valid (will be validated by required check)
  const numValue = Number(value);
  if (isNaN(numValue)) {
    return "Invalid format";
  }
  if (numValue < 3) {
    return "Must be 3 or greater";
  }
  if (numValue > 65535) {
    return "Must be 65535 or less";
  }
  return "";
};

/**
 * Validation function for keepalive-interval: min 1, max < hold-time
 * @param value - The keepalive interval value to validate
 * @param currentHoldTime - The current hold time value to compare against
 * @returns Error message if invalid, empty string if valid
 */
export const validateKeepaliveInterval = (value: string, currentHoldTime: string): string => {
  if (!value) return ""; // Empty is valid (will be validated by required check)
  const numValue = Number(value);
  const holdTimeNum = Number(currentHoldTime) || 0;
  if (isNaN(numValue)) {
    return "Invalid format";
  }
  if (numValue < 1) {
    return "Must be 1 or greater";
  }
  if (holdTimeNum > 0 && numValue >= holdTimeNum) {
    return `Must be less than hold time (${holdTimeNum})`;
  }
  if (numValue > 65535) {
    return "Must be 65535 or less";
  }
  return "";
};

/**
 * Validation function for connect-retry: min 1, max 65535
 * @param value - The connect retry value to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateConnectRetry = (value: string): string => {
  if (!value) return ""; // Empty is valid (will be validated by required check)
  const numValue = Number(value);
  if (isNaN(numValue)) {
    return "Invalid format";
  }
  if (numValue < 1) {
    return "Must be 1 or greater";
  }
  if (numValue > 65535) {
    return "Must be 65535 or less";
  }
  return "";
};

export type IpFamily = 'ipv4' | 'ipv6';

/** True when `value` is a complete dotted-quad IPv4 address. */
export const isValidIPv4 = (value: string): boolean => {
  if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(value)) return false;
  return value.split('.').every(part => Number(part) <= 255);
};

/**
 * True when `value` is a complete IPv6 address in hex-group notation, with at most one `::`.
 * The unspecified address `::` is rejected: it can never be a BGP peer.
 */
export const isValidIPv6 = (value: string): boolean => {
  if (value === '::' || !/^[0-9a-fA-F:]+$/.test(value)) return false;
  const halves = value.split('::');
  if (halves.length > 2) return false;
  const groupsOf = (s: string) => (s === '' ? [] : s.split(':'));
  const groups = [...groupsOf(halves[0]), ...(halves.length === 2 ? groupsOf(halves[1]) : [])];
  if (groups.some(group => !/^[0-9a-fA-F]{1,4}$/.test(group))) return false;
  return halves.length === 2 ? groups.length <= 7 : groups.length === 8;
};

/** Family of a complete IP address, or null when `value` is not a complete address. */
export const getIpFamily = (value: string): IpFamily | null => {
  const trimmed = value.trim();
  if (isValidIPv4(trimmed)) return 'ipv4';
  if (isValidIPv6(trimmed)) return 'ipv6';
  return null;
};

/**
 * Family an address is heading towards while it is still being typed: a hex letter or a colon
 * means IPv6, digits and dots mean IPv4. Null when it is empty or cannot tell yet.
 */
const guessIpFamily = (value: string): IpFamily | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/[a-fA-F:]/.test(trimmed)) return 'ipv6';
  if (/^[0-9.]+$/.test(trimmed) && trimmed.includes('.')) return 'ipv4';
  return null;
};

/**
 * Keeps only the source addresses of the same family as the neighbor address.
 * Uses the complete family when the neighbor is a full address, the likely one while it is typed,
 * and returns every option when the family cannot be told yet.
 */
export const filterAddressOptionsByFamily = <T extends { label: string }>(
  options: T[],
  neighborAddress: string
): T[] => {
  const family = getIpFamily(neighborAddress) ?? guessIpFamily(neighborAddress);
  if (!family) return options;
  return options.filter(option => getIpFamily(option.label) === family);
};

/** Progressive IPv6 check: flags only what can never become a valid address. */
const validateIPv6Partial = (value: string): string => {
  if (!/^[0-9a-fA-F:]*$/.test(value)) return "Invalid format";
  if (value.includes(':::') || value.split('::').length > 2) return "Invalid format";
  const groups = value.split(':').filter(group => group !== '');
  if (groups.some(group => group.length > 4)) return "Invalid format";
  if (groups.length > (value.includes('::') ? 7 : 8)) return "Invalid format";
  return "";
};

/**
 * Validation function for IP address (IPv4 or IPv6)
 * Checks the value while it is typed: an incomplete address is not an error here.
 * Use `getIpFamily` to require a complete address before submitting.
 * @param value - The IP address value to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateIPAddress = (value: string): string => {
  if (!value) return ""; // Empty is valid (will be validated by required check)
  if (/[a-fA-F:]/.test(value)) {
    return validateIPv6Partial(value);
  }
  // IPv4: allow numbers and dots only
  const validPattern = /^[0-9.]*$/;
  if (!validPattern.test(value)) {
    return "Invalid format";
  }
  // Basic IP format validation
  const ipParts = value.split('.');
  if (ipParts.length > 4) {
    return "Invalid format";
  }
  // Validate each completed segment
  const isCompleteSegment = value.endsWith('.') || ipParts.length === 4;
  for (let i = 0; i < ipParts.length; i++) {
    const part = ipParts[i];
    if (part && (i < ipParts.length - 1 || isCompleteSegment)) {
      const numValue = parseInt(part);
      if (isNaN(numValue) || numValue > 255 || numValue < 0) {
        return "Invalid format";
      }
    }
  }
  return "";
};

/**
 * Status formatting functions for BGP neighbors
 */

/**
 * Formats status text to Title Case (handles all-caps, all-lowercase, or mixed case)
 * @param status - The status string to format (e.g., "ESTABLISHED", "established", "EsTaBlIsHeD")
 * @returns Formatted status in Title Case (e.g., "Established") or empty string if invalid
 */
export const formatStatusText = (status: string | undefined): string => {
  const trimmedStatus = status?.trim();
  if (!trimmedStatus || trimmedStatus.length === 0) {
    return "";
  }
  
  // Normalize to uppercase first, then format: first letter uppercase, rest lowercase
  const statusUpper = trimmedStatus.toUpperCase();
  return statusUpper.charAt(0) + statusUpper.slice(1).toLowerCase();
};

/**
 * Builds the full status label with optional timestamp
 * @param status - The status string
 * @param statusChangedAt - Optional timestamp (number or Date)
 * @returns Formatted label string (e.g., "Established - 2d 5h" or "Established")
 */
export const buildStatusLabel = (
  status: string | undefined,
  statusChangedAt?: number | Date
): string => {
  const formattedStatus = formatStatusText(status);
  
  if (!formattedStatus) {
    return "-";
  }
  
  if (statusChangedAt) {
    return `${formattedStatus} - ${formatCiscoUptime(statusChangedAt)}`;
  }
  
  return formattedStatus;
};

/**
 * Wire values that mean "no status reported", as opposed to a real BGP FSM state. There is no
 * FSM enum in the model — session status is a free string — so this is a literal comparison,
 * deliberately, and the list stays small: anything not in it and not ESTABLISHED is a genuine
 * non-established state (Idle / Active / Connect / OpenSent / …).
 */
const UNREPORTED_SESSION_STATUSES = new Set(['UNKNOWN', 'N/A', '-']);

/**
 * Whether a session is established. Case-insensitive literal match on 'ESTABLISHED' — the same
 * comparison every session surface uses; import this rather than re-inlining `.toUpperCase()`.
 */
export const isSessionEstablished = (status: string | undefined | null): boolean =>
  (status ?? '').trim().toUpperCase() === 'ESTABLISHED';

/** Whether the peer actually reported a state, vs. the UI defaulting to "unknown". */
export const isSessionStatusReported = (status: string | undefined | null): boolean => {
  const trimmed = (status ?? '').trim();
  return trimmed.length > 0 && !UNREPORTED_SESSION_STATUSES.has(trimmed.toUpperCase());
};

/**
 * A peer that reported a session state other than ESTABLISHED — i.e. genuinely down (Idle /
 * Active / Connect / OpenSent / …), as distinct from one whose state never arrived.
 *
 * The single predicate for "this peer is down", so the topology, the derivation below and any
 * future surface cannot disagree about which peers count as down. An UNREPORTED status is
 * deliberately NOT down: we do not know, and claiming an outage we cannot see is worse than
 * staying silent.
 */
export const isSessionDown = (status: string | undefined | null): boolean =>
  isSessionStatusReported(status) && !isSessionEstablished(status);

/**
 * Tag variant for a BGP session status.
 *
 * Three-way on purpose: a session sitting in Idle/Active/Connect is *failing*, which is not the
 * same as one whose state we never received. Collapsing both to a neutral grey hid real outages,
 * so a reported non-established state now reads as danger while an unreported one stays neutral.
 *
 * @param status - The status string
 * @returns 'success' for ESTABLISHED, 'error' for a reported non-established state,
 *          'default' when nothing was reported
 */
export const getStatusVariant = (status: string | undefined): 'success' | 'error' | 'default' => {
  if (!isSessionStatusReported(status)) {
    return 'default';
  }
  return isSessionEstablished(status) ? 'success' : 'error';
};

/**
 * Label for a session cell whose reflector stopped reporting (powered off or
 * unreachable). The persisted status is frozen at its last value, so the cell
 * must say "no data" instead of presenting the stale state as live.
 * @param reflectorLastUpdate - Timestamp of the reflector's last keepalive
 * @returns Label string (e.g., "No data — reflector down · 10 minutes, 5 seconds")
 */
export const buildReflectorDownLabel = (
  reflectorLastUpdate?: string | number | Date | null
): string => {
  if (!reflectorLastUpdate) {
    return 'No data — reflector down';
  }
  return `No data — reflector down · ${formatCiscoUptime(new Date(reflectorLastUpdate))}`;
};

/** Title shared by every "reflector stopped reporting" surface (cell label, topology overlay). */
export const REFLECTOR_DOWN_TITLE = 'No data — reflector down';

/**
 * How long a reflector has been unresponsive, as an elapsed-time phrase on its own
 * (e.g. "1 minute, 38 seconds") — the same figure `buildReflectorDownLabel` appends, but
 * without the title, so the topology down-overlay can compose its own sentence around it.
 * @param reflectorLastUpdate - Timestamp of the reflector's last keepalive
 * @returns The elapsed phrase, or null when no timestamp is known
 */
export const buildReflectorDownElapsed = (
  reflectorLastUpdate?: string | number | Date | null
): string | null => {
  if (!reflectorLastUpdate) {
    return null;
  }
  const date = new Date(reflectorLastUpdate);
  return Number.isNaN(date.getTime()) ? null : formatCiscoUptime(date);
};

/**
 * Body sentence for the topology down-overlay: names the down member by role + reflector name
 * and states how long it has been unresponsive, then warns that nothing behind the overlay
 * could be verified.
 * @param memberLabel - Role + name of the down member (e.g. "Primary CTXR-5cb248")
 * @param reflectorLastUpdate - Timestamp of the member's last keepalive
 */
export const buildReflectorDownBody = (
  memberLabel: string,
  reflectorLastUpdate?: string | number | Date | null
): string => {
  const elapsed = buildReflectorDownElapsed(reflectorLastUpdate);
  const subject = memberLabel || 'This reflector';
  return elapsed
    ? `${subject} has not responded for ${elapsed}. Nothing shown behind this message could be verified.`
    : `${subject} is not responding. Nothing shown behind this message could be verified.`;
};

/**
 * Card title for the license gate, per reason.
 *
 * A single "License expired" title was wrong for three of the five reasons: it sat above a body
 * saying the license was never assigned, or suspended, contradicting itself. The title is the
 * first thing read, so it has to agree with the sentence under it — and each state points at a
 * different action (renew, escalate, wait, assign).
 *
 * @param reason - The validity reason from the license-validity endpoint
 */
export const buildLicenseGateTitle = (reason: string): string => {
  switch (reason) {
    case 'expired':
      return 'License expired';
    case 'suspended':
      return 'License suspended';
    case 'not_started':
      return 'License not active yet';
    case 'no_license':
      return 'No license assigned';
    default:
      return 'License not valid';
  }
};

/** Path the gate's CTA lands on, so the four call sites cannot point at different routes. */
export const LICENSING_PATH = '/reflector-v2/management/licensing';

/**
 * A license expiry date as the gate states it. Kept separate from the sentence builder so a
 * malformed or absent date degrades to no phrase at all rather than "Invalid Date".
 * @param endDate - The license's `endDate` from the validity endpoint
 * @returns A localised date string, or null when there is no usable date
 */
export const formatLicenseExpiry = (
  endDate?: string | number | Date | null
): string | null => {
  if (!endDate) {
    return null;
  }
  const date = new Date(endDate);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
};

/**
 * Body sentence for the license gate: names the reflector, states why its license is not
 * valid, and points at Licensing. The four gated views share this so their copy cannot drift.
 *
 * `reason` carries the backend's distinction, which matters to the reader: a lapsed license is
 * renewed, a suspended one is escalated, and one that was never assigned is a different
 * conversation entirely. An unknown reason falls back to the neutral phrasing rather than
 * asserting something the API did not say.
 *
 * @param subject - How to name the target (e.g. "CTXR-5cb248", or "Primary CTXR-5cb248" in a cluster)
 * @param reason - The validity reason from the CTX-345 endpoint
 * @param endDate - The license's expiry date, when there is one
 */
export const buildLicenseExpiredBody = (
  subject: string,
  reason: string,
  endDate?: string | number | Date | null
): string => {
  const who = subject || 'This reflector';
  const on = formatLicenseExpiry(endDate);

  switch (reason) {
    case 'expired':
      return on
        ? `${who} has a license that expired on ${on}. Renew it to manage this reflector again — what is shown behind this message is the last known state.`
        : `${who} has an expired license. Renew it to manage this reflector again — what is shown behind this message is the last known state.`;
    case 'suspended':
      return `${who} has a suspended license. Contact your account manager to reactivate it — what is shown behind this message is the last known state.`;
    case 'not_started':
      return on
        ? `${who} has a license that does not start until ${on}. What is shown behind this message is the last known state.`
        : `${who} has a license that has not started yet. What is shown behind this message is the last known state.`;
    case 'no_license':
      return `${who} has no license assigned. Assign one to manage this reflector — what is shown behind this message is the last known state.`;
    default:
      return `${who} does not have a valid license. What is shown behind this message is the last known state.`;
  }
};

/**
 * Display form for an AWS resource tag: `name : value`. The single place that decides how a tag
 * reads, so the detail modal, the topology card and any future surface cannot drift apart.
 *
 * A tag with an empty value renders as the bare name — AWS allows valueless tags, and
 * "Environment :" would look truncated.
 */
export const formatCloudResourceTag = (tag: CloudResourceTag): string =>
  tag.value ? `${tag.name} : ${tag.value}` : tag.name;

/**
 * Every tag on an attribution, formatted for display. Outbound rules can match a destination on
 * several tags at once, so callers must handle more than one — an empty array means the payload
 * carried no tags (pinned destination, or a document predating the field).
 */
export const formatTagAttribution = (attribution: TagAttribution | null): string[] =>
  (attribution?.tags ?? []).map(formatCloudResourceTag);

/** A neighbor's session as the sessions-down derivation needs to see it. */
export interface BgpSessionNeighbor {
  neighborIp: string;
  status?: string | null;
}

export interface BgpSessionState {
  /** Reflector is up, there is at least one neighbor, and none of them are established. */
  allSessionsDown: boolean;
  neighborCount: number;
  establishedCount: number;
  lastUpdate: string | null;
}

export interface DeriveBgpSessionStateParams {
  /** Neighbors in the current direction, with whatever status the reflector reported. */
  neighbors: BgpSessionNeighbor[];
  /** Cluster members. Empty in single-reflector mode — see `reflectorHasData`. */
  members: ClusterMemberCpr[];
  /**
   * Single-reflector fallback for "the reflector is reporting": there are no cluster members to
   * run `isClusterMemberUp` against, and a reflector that returned a document satisfies the same
   * notion of verified that predicate's `cpr !== null` half expresses.
   */
  reflectorHasData?: boolean;
  lastUpdate?: string | null;
}

/**
 * Derives the "reflector online but nothing is peering" state (single source of truth — import
 * this, never re-inline the predicate).
 *
 * Requires a **reporting** reflector, which is what makes this state meaningfully different
 * from reflector-down: an up reflector saying "my sessions are down" is verified information,
 * so the canvas stays visible and unblurred and only gets a warning banner. When no member is
 * up the observation is unverifiable and the reflector-down overlay owns the canvas instead —
 * gating on liveness here is what keeps the two states mutually exclusive.
 */
export const deriveBgpSessionState = ({
  neighbors,
  members,
  reflectorHasData = false,
  lastUpdate = null
}: DeriveBgpSessionStateParams): BgpSessionState => {
  const neighborCount = neighbors.length;
  const establishedCount = neighbors.filter(neighbor => isSessionEstablished(neighbor.status)).length;
  const reflectorIsReporting = members.length > 0 ? members.some(isClusterMemberUp) : reflectorHasData;

  return {
    allSessionsDown: reflectorIsReporting && neighborCount > 0 && establishedCount === 0,
    neighborCount,
    establishedCount,
    lastUpdate
  };
};

/** Every peer in the roster, in the shape `deriveBgpSessionState` consumes. */
export const rosterSessionNeighbors = (roster: BgpNeighborRoster): BgpSessionNeighbor[] =>
  Object.entries(roster).map(([neighborIp, info]) => ({ neighborIp, status: info?.status ?? null }));

/**
 * Orders peer IPs ascending, comparing IPv4 octets NUMERICALLY.
 *
 * The single ordering rule for neighbors on the canvas, so the live cards and the down cards
 * below them cannot drift apart. A plain string compare is wrong here: it puts `172.23.9.1`
 * after `172.23.166.57`, because it compares `9` against `1` character-by-character.
 *
 * Anything that is not a dotted quad of numbers (IPv6, a malformed roster key) falls back to a
 * string compare and sorts after the IPv4 addresses — unordered is worse than arbitrary-but-stable,
 * which is the whole point of this comparator.
 */
export const compareNeighborIp = (a: string, b: string): number => {
  const octetsOf = (ip: string): number[] | null => {
    const parts = ip.split('.');
    if (parts.length !== 4) return null;
    const octets = parts.map(part => (/^\d{1,3}$/.test(part) ? Number(part) : NaN));
    return octets.some(octet => Number.isNaN(octet) || octet > 255) ? null : octets;
  };

  const left = octetsOf(a);
  const right = octetsOf(b);

  if (!left || !right) {
    // Both unparseable → stable string order; one parseable → real IPv4 addresses come first.
    if (!left && !right) return a.localeCompare(b);
    return left ? -1 : 1;
  }

  for (let i = 0; i < 4; i += 1) {
    if (left[i] !== right[i]) return left[i] - right[i];
  }
  return 0;
};

/** A peer reported down, carried to the topology so it can be drawn without any routes. */
export interface DownNeighbor {
  neighborIp: string;
  /** The reported FSM state that made it down (e.g. 'Idle'), already title-cased for display. */
  sessionStatus: string;
  peerAs?: number;
  description?: string;
}

/**
 * Peers the reflector reports as DOWN that contributed no rows to the drawn set.
 *
 * These are invisible to every route-derived surface: a down session exchanges no prefixes, so it
 * produces no `RouteRow`, so the topology never emits a node for it and the operator cannot
 * distinguish a peer that is down from one that does not exist. Reading the roster here is what
 * puts them back on the canvas.
 *
 * Restricted to peers that are down *and* route-less on purpose:
 *  - route-less but ESTABLISHED is not an outage, and injecting it would make route filters look
 *    like they had matched a peer with nothing to match;
 *  - a down peer still holding stale prefixes already HAS a node, so re-emitting it would
 *    duplicate the card.
 *
 * Sorted by `compareNeighborIp` — the same comparator the live neighbor cards use — so node order,
 * and therefore vertical position on the canvas, is stable across renders and consistent between
 * the two groups, rather than following object-key order from the API.
 */
export const deriveDownNeighborsWithoutRoutes = (
  roster: BgpNeighborRoster,
  neighborIpsWithRoutes: Iterable<string>
): DownNeighbor[] => {
  const withRoutes = new Set(neighborIpsWithRoutes);

  return Object.entries(roster)
    .filter(([neighborIp, info]) => !withRoutes.has(neighborIp) && isSessionDown(info?.status))
    .map(([neighborIp, info]) => ({
      neighborIp,
      sessionStatus: formatStatusText(info?.status),
      peerAs: info?.peerAs,
      description: info?.description
    }))
    .sort((a, b) => compareNeighborIp(a.neighborIp, b.neighborIp));
};

/**
 * Checks if a status value exists and has content
 * @param status - The status string to check
 * @returns true if status exists and has content, false otherwise
 */
export const hasStatus = (status: string | undefined): boolean => {
  const trimmedStatus = status?.trim();
  return !!(trimmedStatus && trimmedStatus.length > 0);
};

/**
 * BFD session label from daemon telemetry (bfdLocalState / bfdRemoteState).
 * Callers must check the `BFD` config boolean first:
 *   BFD === false  →  show "Inactive" tag directly (not via this helper)
 *   BFD === true   →  call buildBfdLabel; null means no telemetry yet → show "-"
 */
export const buildBfdLabel = (
  bfdEnabled: boolean | undefined,
  localState: string | undefined,
  remoteState: string | undefined,
  bfdStatusChangedAt?: number,
): string | null => {
  if (!bfdEnabled) return null;
  if (!localState || !remoteState) return null;

  const stateStr = `${localState}/${remoteState}`;
  if (bfdStatusChangedAt) {
    return `${stateStr} - ${formatCiscoUptime(bfdStatusChangedAt)}`;
  }
  return stateStr;
};

/**
 * Builds the BFD daemon telemetry detail line.
 * Only uses daemon-reported runtime values (tx, rx, multiplier).
 * Returns null when no daemon telemetry is available.
 */
export const buildBfdDetail = (
  bfdEnabled: boolean | undefined,
  daemonTx?: number,
  daemonRx?: number,
  daemonMultiplier?: number,
): string | null => {
  if (!bfdEnabled) return null;
  if (daemonTx == null && daemonRx == null && daemonMultiplier == null) return null;
  const parts: string[] = [];
  if (daemonTx != null) parts.push(`tx=${daemonTx}ms`);
  if (daemonRx != null) parts.push(`rx=${daemonRx}ms`);
  if (daemonMultiplier != null) parts.push(`multiplier=${daemonMultiplier}`);
  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * Determines the tag variant for a BFD status label.
 */
export const getBfdVariant = (
  bfdEnabled: boolean | undefined,
  localState: string | undefined,
  remoteState: string | undefined
): 'success' | 'error' | 'warning' | 'default' => {
  if (!bfdEnabled) return 'default';
  if (!localState || !remoteState) return 'default';
  const local = localState.toUpperCase();
  const remote = remoteState.toUpperCase();
  if (local === 'UP' && remote === 'UP') return 'success';
  if (local === 'DOWN' || remote === 'DOWN') return 'error';
  return 'warning';
};

/**
 * Builds the prefix counters label: "Pfx: 5/5/10 (rcvd/accepted/adv)"
 * Returns null when no counter data is available.
 */
export const buildPrefixLabel = (
  received: number | undefined,
  accepted: number | undefined,
  advertised: number | undefined,
): string | null => {
  if (received == null && accepted == null && advertised == null) return null;
  const r = received ?? 0;
  const a = accepted ?? 0;
  const adv = advertised ?? 0;
  return `Pfx ${r}/${a}/${adv}`;
};

